import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";
import { Alert, AlertDescription, AlertTitle } from "../../../app/components/ui/alert";
import { Button } from "../../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../app/components/ui/card";
import { Input } from "../../../app/components/ui/input";
import { Label } from "../../../app/components/ui/label";
import { Separator } from "../../../app/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../app/components/ui/table";
import { Textarea } from "../../../app/components/ui/textarea";
import { Badge } from "../../../app/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../app/components/ui/select";
import { Trash2 } from "lucide-react";
import { DealerSelector } from "../../../components/selectors/DealerSelector";
import { ProductSearchDropdown } from "../components/ProductSearchDropdown";
import { api } from "../../../lib/api";
import { useAuth } from "../../../providers/AuthProvider";

type GstRegime = "igst" | "cgst_sgst" | "unknown";

/** Footer HSN/SAC tax table — whole invoice (typical dealer → shop). */
type GstInvoiceSummary = {
  hsn_sac: string;
  taxable_value: number;
  central_tax_rate_pct: number;
  central_tax_amount: number;
  state_tax_rate_pct: number;
  state_tax_amount: number;
  integrated_tax_rate_pct: number;
  integrated_tax_amount: number;
  total_tax_amount: number;
  total_quantity_display: string;
  amount_chargeable_in_words: string;
  tax_amount_in_words: string;
};

const EMPTY_GST_INVOICE_SUMMARY: GstInvoiceSummary = {
  hsn_sac: "",
  taxable_value: 0,
  central_tax_rate_pct: 0,
  central_tax_amount: 0,
  state_tax_rate_pct: 0,
  state_tax_amount: 0,
  integrated_tax_rate_pct: 0,
  integrated_tax_amount: 0,
  total_tax_amount: 0,
  total_quantity_display: "",
  amount_chargeable_in_words: "",
  tax_amount_in_words: "",
};

function parseGstInvoiceSummary(raw: unknown): GstInvoiceSummary {
  if (!raw || typeof raw !== "object") {
    return { ...EMPTY_GST_INVOICE_SUMMARY };
  }
  const o = raw as Record<string, unknown>;
  const str = (k: string) => {
    const v = o[k];
    if (v === null || v === undefined) return "";
    return String(v);
  };
  const num = (k: string) => Number(o[k] ?? 0);
  return {
    hsn_sac: str("hsn_sac"),
    taxable_value: num("taxable_value"),
    central_tax_rate_pct: num("central_tax_rate_pct"),
    central_tax_amount: num("central_tax_amount"),
    state_tax_rate_pct: num("state_tax_rate_pct"),
    state_tax_amount: num("state_tax_amount"),
    integrated_tax_rate_pct: num("integrated_tax_rate_pct"),
    integrated_tax_amount: num("integrated_tax_amount"),
    total_tax_amount: num("total_tax_amount"),
    total_quantity_display: str("total_quantity_display"),
    amount_chargeable_in_words: str("amount_chargeable_in_words"),
    tax_amount_in_words: str("tax_amount_in_words"),
  };
}

function gstInvoiceSummaryHasData(s: GstInvoiceSummary): boolean {
  return (
    s.hsn_sac.trim() !== "" ||
    s.total_quantity_display.trim() !== "" ||
    s.amount_chargeable_in_words.trim() !== "" ||
    s.tax_amount_in_words.trim() !== "" ||
    s.taxable_value > 0 ||
    s.central_tax_amount > 0 ||
    s.state_tax_amount > 0 ||
    s.integrated_tax_amount > 0 ||
    s.total_tax_amount > 0
  );
}

type PreviewItem = {
  name: string;
  qty: number;
  purchase_price: number;
  selling_price: number;
  mrp: number;
  product_id: number | null;
  product_name?: string | null;
  mapping_confidence?: number;
  mapping_source?: string;
  cgst_rate_pct?: number;
  cgst_amount?: number;
  sgst_rate_pct?: number;
  sgst_amount?: number;
  igst_rate_pct?: number;
  igst_amount?: number;
};

export function InvoiceUploadPage() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const previewObjectUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
    };
  }, [previewObjectUrl]);
  const [uploading, setUploading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [committing, setCommitting] = useState(false);

  const [rootError, setRootError] = useState<string | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);

  const [dealerId, setDealerId] = useState<string>("");
  const [dealerName, setDealerName] = useState<string>("");
  const [dealerContactPerson, setDealerContactPerson] = useState<string>("");
  const [dealerPhone, setDealerPhone] = useState<string>("");
  const [dealerEmail, setDealerEmail] = useState<string>("");
  const [dealerGstNumber, setDealerGstNumber] = useState<string>("");
  const [dealerAddress, setDealerAddress] = useState<string>("");

  const [referenceNo, setReferenceNo] = useState<string>("");
  const [totalAmount, setTotalAmount] = useState<string>("");
  const [gstRegime, setGstRegime] = useState<GstRegime>("unknown");
  const [gstColumnMap, setGstColumnMap] = useState<string>("");
  const [gstInvoiceSummary, setGstInvoiceSummary] = useState<GstInvoiceSummary>(() => ({ ...EMPTY_GST_INVOICE_SUMMARY }));

  const [items, setItems] = useState<PreviewItem[]>([]);
  // Baseline snapshot used to detect whether the user changed mapping-relevant fields
  // (product_id selection and/or the editable "Name" field) before committing.
  const baselineItemsRef = useRef<Array<{ name: string; product_id: number | null }>>([]);
  const [itemConfidence, setItemConfidence] = useState<number[]>([]);
  const [dealerConfidence, setDealerConfidence] = useState<number | null>(null);
  const [autoRefreshStopped, setAutoRefreshStopped] = useState(false);
  const pollAttemptsRef = useRef(0);

  const lowConfidenceThreshold = 0.6;
  const mappingRequiredThreshold = 0.8;

  const canUpload = Boolean(token && file && !uploading);
  const mappingRequiredItemRows = useMemo(() => {
    return new Set(
      items
        .map((it, i) => ({ i, requires: (it.mapping_confidence ?? 0) > mappingRequiredThreshold && !it.product_id }))
        .filter((x) => x.requires)
        .map((x) => x.i),
    );
  }, [items]);
  const canCommit = Boolean(
    token && uploadId && items.length > 0 && !committing && (dealerId || dealerName) && mappingRequiredItemRows.size === 0,
  );

  const lowConfidenceItemRows = useMemo(() => {
    return new Set(itemConfidence.map((c, i) => ({ c, i })).filter((x) => x.c < lowConfidenceThreshold).map((x) => x.i));
  }, [itemConfidence]);

  const removeItemRow = useCallback((idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setItemConfidence((prev) => prev.filter((_, i) => i !== idx));
    baselineItemsRef.current = baselineItemsRef.current.filter((_, i) => i !== idx);
  }, []);

  async function onUpload() {
    if (!token) return;
    if (!file) {
      setRootError("Please select a JPG or PNG image of your invoice.");
      return;
    }
    const lower = file.name.toLowerCase();
    if (lower.endsWith(".pdf") || file.type === "application/pdf") {
      setRootError(
        "PDF uploads are not supported. Please upload a JPG or PNG photo of your invoice to save API usage and server resources.",
      );
      return;
    }
    setRootError(null);
    setUploading(true);
    setAutoRefreshStopped(false);
    pollAttemptsRef.current = 0;
    setUploadId(null);
    setStatus(null);
    setItems([]);
    baselineItemsRef.current = [];
    setDealerId("");
    setDealerName("");
    setDealerContactPerson("");
    setDealerPhone("");
    setDealerEmail("");
    setDealerGstNumber("");
    setDealerAddress("");
    setDealerConfidence(null);
    setItemConfidence([]);
    setReferenceNo("");
    setTotalAmount("");
    setGstRegime("unknown");
    setGstColumnMap("");
    setGstInvoiceSummary({ ...EMPTY_GST_INVOICE_SUMMARY });

    try {
      const res = await api.inventory.invoiceUpload({ token, file });
      setUploadId(res.upload_id);
      setStatus(res.status);
      await onPreview(res.upload_id);
    } catch (e) {
      setRootError(e instanceof Error ? e.message : "Failed to upload invoice.");
    } finally {
      setUploading(false);
    }
  }

  const onPreview = useCallback(async (id: string) => {
    if (!token) return;
    setAutoRefreshStopped(false);
    setRootError(null);
    setLoadingPreview(true);
    try {
      const res = await api.inventory.invoicePreview({ token, id });
      setStatus(res.status);

      const data = res.data as unknown as {
        dealer:
          | {
              name?: string | null;
              dealer_id?: number | null;
              contact_person?: string | null;
              phone?: string | null;
              email?: string | null;
              gst_number?: string | null;
              address?: string | null;
            }
          | null;
        items: Array<PreviewItem & Record<string, unknown>>;
        confidence?: { dealer?: number; items?: number[] };
        totals?: { subtotal?: number; tax?: number; grand_total?: number };
        gst?: { regime?: string; column_map?: string; invoice_summary?: unknown };
      };

      const dName = data.dealer?.name ?? "";
      const dId = data.dealer?.dealer_id ? String(data.dealer.dealer_id) : "";
      setDealerName(dName);
      setDealerId(dId);
      setDealerContactPerson(data.dealer?.contact_person ?? "");
      setDealerPhone(data.dealer?.phone ?? "");
      setDealerEmail(data.dealer?.email ?? "");
      setDealerGstNumber(data.dealer?.gst_number ?? "");
      setDealerAddress(data.dealer?.address ?? "");

      setDealerConfidence(typeof data.confidence?.dealer === "number" ? data.confidence?.dealer : null);
      setItemConfidence(Array.isArray(data.confidence?.items) ? data.confidence?.items : []);

      const nextItems: PreviewItem[] = (data.items ?? []).map((it) => {
        const mrp = Number(it.mrp ?? 0);
        const selling = it.selling_price !== undefined && it.selling_price !== null ? Number(it.selling_price) : mrp;

        return {
          name: it.name ?? "",
          qty: Number(it.qty ?? 0),
          purchase_price: Number(it.purchase_price ?? 0),
          selling_price: selling,
          mrp,
          product_id: it.product_id ?? null,
          product_name: it.product_name ?? null,
          mapping_confidence:
            it.mapping_confidence !== undefined && it.mapping_confidence !== null ? Number(it.mapping_confidence) : 0,
          mapping_source: it.mapping_source ?? undefined,
          cgst_rate_pct: Number(it.cgst_rate_pct ?? 0),
          cgst_amount: Number(it.cgst_amount ?? 0),
          sgst_rate_pct: Number(it.sgst_rate_pct ?? 0),
          sgst_amount: Number(it.sgst_amount ?? 0),
          igst_rate_pct: Number(it.igst_rate_pct ?? 0),
          igst_amount: Number(it.igst_amount ?? 0),
        };
      });

      setItems(nextItems);
      baselineItemsRef.current = nextItems.map((x) => ({ name: x.name, product_id: x.product_id }));

      setReferenceNo(`INV-${new Date().toISOString().slice(0, 10)}-${id}`);

      const grand = data.totals?.grand_total != null ? Number(data.totals.grand_total) : 0;
      if (grand > 0) {
        setTotalAmount(grand.toFixed(2));
      } else {
        const sum = (data.items ?? []).reduce((acc, it) => acc + Number(it.qty ?? 0) * Number(it.purchase_price ?? 0), 0);
        setTotalAmount(sum > 0 ? sum.toFixed(2) : "");
      }

      const regime = (data.gst?.regime ?? "unknown").toLowerCase();
      if (regime === "igst" || regime === "cgst_sgst" || regime === "unknown") {
        setGstRegime(regime);
      } else {
        setGstRegime("unknown");
      }
      setGstColumnMap(typeof data.gst?.column_map === "string" ? data.gst.column_map : "");
      setGstInvoiceSummary(parseGstInvoiceSummary(data.gst?.invoice_summary));
    } catch (e) {
      setRootError(e instanceof Error ? e.message : "Failed to load preview.");
    } finally {
      setLoadingPreview(false);
    }
  }, [token]);

  useEffect(() => {
    if (!uploadId) return;
    if (status !== "processing") return;
    pollAttemptsRef.current = 0;
    setAutoRefreshStopped(false);
  }, [uploadId, status]);

  useEffect(() => {
    if (!uploadId) return;
    if (status !== "processing") return;
    if (loadingPreview) return;
    if (autoRefreshStopped) return;

    const maxAttempts = 60;
    if (pollAttemptsRef.current >= maxAttempts) {
      setAutoRefreshStopped(true);
      return;
    }

    const attempt = pollAttemptsRef.current;
    const delayMs = Math.min(5000, 1000 * Math.pow(2, Math.floor(attempt / 10)));

    const timer = window.setTimeout(() => {
      // Re-check current conditions to avoid firing after status changed.
      if (!uploadId) return;
      if (status !== "processing") return;
      if (loadingPreview) return;
      if (autoRefreshStopped) return;

      pollAttemptsRef.current += 1;
      void onPreview(uploadId);
    }, delayMs);

    return () => window.clearTimeout(timer);
  }, [uploadId, status, loadingPreview, autoRefreshStopped, onPreview]);

  async function onCommit() {
    if (!token) return;
    if (!uploadId) return;
    if (!canCommit) return;

    setRootError(null);
    setCommitting(true);
    try {
      const gstMetaPayload: { column_map?: string; invoice_summary?: GstInvoiceSummary } = {};
      if (gstColumnMap.trim()) {
        gstMetaPayload.column_map = gstColumnMap.trim();
      }
      if (gstInvoiceSummaryHasData(gstInvoiceSummary)) {
        gstMetaPayload.invoice_summary = { ...gstInvoiceSummary };
      }
      const gst_meta = Object.keys(gstMetaPayload).length > 0 ? gstMetaPayload : undefined;

      const res = await api.inventory.invoiceCommit({
        token,
        id: uploadId,
        payload: {
          reference_no: referenceNo,
          total_amount: totalAmount,
          gst_regime: gstRegime,
          gst_meta,
          dealer: dealerId
            ? { dealer_id: dealerId }
            : {
                name: dealerName,
                status: "active",
                contact_person: dealerContactPerson.trim() || null,
                phone: dealerPhone.trim() || null,
                email: dealerEmail.trim() || null,
                gst_number: dealerGstNumber.trim() || null,
                address: dealerAddress.trim() || null,
              },
          items: items.map((it, idx) => {
            const base = baselineItemsRef.current[idx];
            const normalizedCurrentName = (it.name ?? "").trim();
            const normalizedBaseName = (base?.name ?? "").trim();
            const normalizedCurrentProductId = it.product_id ?? null;
            const normalizedBaseProductId = base?.product_id ?? null;

            // Mark as corrected only when mapping-relevant fields changed:
            // 1) product_id selection
            // 2) editable "Name" field (parsed invoice line text)
            const is_user_corrected =
              normalizedCurrentName !== normalizedBaseName || normalizedCurrentProductId !== normalizedBaseProductId;

            return {
              product_id: it.product_id ?? null,
              name: it.name,
              raw_name: it.name,
              is_user_corrected,
              qty: it.qty,
              purchase_price: it.purchase_price,
              selling_price: it.selling_price,
              mrp: it.mrp,
              cgst_rate_pct: it.cgst_rate_pct ?? 0,
              cgst_amount: it.cgst_amount ?? 0,
              sgst_rate_pct: it.sgst_rate_pct ?? 0,
              sgst_amount: it.sgst_amount ?? 0,
              igst_rate_pct: it.igst_rate_pct ?? 0,
              igst_amount: it.igst_amount ?? 0,
            };
          }),
        },
      });

      setStatus(res.status);
      if (res.status === "committed" || res.inventory_entry_id) {
        navigate("/inventory");
      }
    } catch (e) {
      setRootError(e instanceof Error ? e.message : "Failed to commit invoice.");
    } finally {
      setCommitting(false);
    }
  }

  return (
    <div className="p-4 space-y-6 md:p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-1 md:text-3xl">Invoice upload (AI)</h1>
          <p className="text-muted-foreground">Upload a kacha bill (image/PDF), verify extracted data, then create inventory.</p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline">
            <Link to="/inventory">Back to inventory</Link>
          </Button>
        </div>
      </div>

      {rootError ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{rootError}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>1) Upload</CardTitle>
          <CardDescription>
            Supported: JPG or PNG only (PDF is not accepted). Use a clear photo of the invoice; the backend runs OCR (e.g. Google Vision).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-1">
              <Input
                type="file"
                accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                onChange={(e) => {
                  const f = e.target.files?.[0] ?? null;
                  setRootError(null);
                  if (f && (f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"))) {
                    setFile(null);
                    setRootError(
                      "PDF uploads are not supported. Please export or screenshot the invoice as JPG or PNG.",
                    );
                    e.target.value = "";
                    return;
                  }
                  setFile(f);
                }}
              />
              {uploadId ? <div className="text-xs text-muted-foreground mt-2">Upload ID: {uploadId} ({status ?? "..."})</div> : null}
            </div>
            <Button type="button" onClick={() => void onUpload()} disabled={!canUpload}>
              {uploading ? "Uploading..." : "Upload & Extract"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {uploadId ? (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>2) Preview & edit</CardTitle>
            <CardDescription>
              Low-confidence fields are highlighted. You can correct dealer, map products, and adjust quantities/prices before committing.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 p-4 lg:p-6">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
              <div className="min-w-0 flex-1 space-y-6">
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => void onPreview(uploadId)} disabled={loadingPreview}>
                {loadingPreview ? "Loading..." : "Refresh preview"}
              </Button>
            </div>

            <Separator />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Reference no</Label>
                <Input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} placeholder="INV-..." />
              </div>
              <div className="space-y-2">
                <Label>Total amount</Label>
                <Input value={totalAmount} onChange={(e) => setTotalAmount(e.target.value)} placeholder="0.00" />
              </div>
              <div className="space-y-2">
                <Label>Dealer</Label>
                <div className="space-y-2">
                  <DealerSelector value={dealerId} onValueChange={(v) => setDealerId(v)} placeholder="Select existing dealer" />
                  <Input
                    value={dealerName}
                    onChange={(e) => setDealerName(e.target.value)}
                    placeholder="Or type dealer name (creates new)"
                    className={dealerConfidence !== null && dealerConfidence < lowConfidenceThreshold ? "border-destructive" : undefined}
                  />
                  {dealerConfidence !== null ? (
                    <div className="text-xs text-muted-foreground">Dealer confidence: {(dealerConfidence * 100).toFixed(0)}%</div>
                  ) : null}
                  {!dealerId ? (
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Contact person</Label>
                        <Input
                          value={dealerContactPerson}
                          onChange={(e) => setDealerContactPerson(e.target.value)}
                          placeholder="Optional"
                          className={dealerConfidence !== null && dealerConfidence < lowConfidenceThreshold ? "border-destructive" : undefined}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input
                          value={dealerPhone}
                          onChange={(e) => setDealerPhone(e.target.value)}
                          placeholder="Optional"
                          className={dealerConfidence !== null && dealerConfidence < lowConfidenceThreshold ? "border-destructive" : undefined}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          value={dealerEmail}
                          onChange={(e) => setDealerEmail(e.target.value)}
                          placeholder="Optional"
                          type="email"
                          className={dealerConfidence !== null && dealerConfidence < lowConfidenceThreshold ? "border-destructive" : undefined}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>GST number</Label>
                        <Input
                          value={dealerGstNumber}
                          onChange={(e) => setDealerGstNumber(e.target.value)}
                          placeholder="Optional"
                          className={dealerConfidence !== null && dealerConfidence < lowConfidenceThreshold ? "border-destructive" : undefined}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Address</Label>
                        <Textarea
                          value={dealerAddress}
                          onChange={(e) => setDealerAddress(e.target.value)}
                          placeholder="Optional"
                          rows={3}
                          className={dealerConfidence !== null && dealerConfidence < lowConfidenceThreshold ? "border-destructive" : undefined}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="space-y-4 w-full max-w-xl min-w-0">
              <div className="space-y-2">
                <Label>GST regime</Label>
                <Select value={gstRegime} onValueChange={(v) => setGstRegime(v as GstRegime)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Regime" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cgst_sgst">CGST + SGST (intrastate)</SelectItem>
                    <SelectItem value="igst">IGST (interstate)</SelectItem>
                    <SelectItem value="unknown">Unknown / mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>AI column map (optional)</Label>
                <Textarea
                  value={gstColumnMap}
                  onChange={(e) => setGstColumnMap(e.target.value)}
                  placeholder="Which printed columns were read as CGST/SGST/IGST"
                  rows={2}
                  className="text-sm"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <div className="font-medium">Invoice GST summary (whole purchase)</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-3xl">
                  Dealer invoices usually show tax once in the footer HSN/SAC table (taxable value, Central/State or
                  Integrated tax, total tax). Line items above may have no per-row GST — the table below is the source of
                  truth for procurement tax on the full bill.
                </p>
              </div>
              <div className="overflow-x-auto rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>HSN/SAC</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Taxable value</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Central %</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Central ₹</TableHead>
                      <TableHead className="text-right whitespace-nowrap">State %</TableHead>
                      <TableHead className="text-right whitespace-nowrap">State ₹</TableHead>
                      <TableHead className="text-right whitespace-nowrap">IGST %</TableHead>
                      <TableHead className="text-right whitespace-nowrap">IGST ₹</TableHead>
                      <TableHead className="text-right whitespace-nowrap">Total tax</TableHead>
                      <TableHead className="whitespace-nowrap">Total qty</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-mono text-sm">{gstInvoiceSummary.hsn_sac || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {gstInvoiceSummary.taxable_value > 0 ? gstInvoiceSummary.taxable_value.toFixed(2) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {gstInvoiceSummary.central_tax_rate_pct > 0 ? `${gstInvoiceSummary.central_tax_rate_pct.toFixed(2)}%` : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {gstInvoiceSummary.central_tax_amount > 0 ? gstInvoiceSummary.central_tax_amount.toFixed(2) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {gstInvoiceSummary.state_tax_rate_pct > 0 ? `${gstInvoiceSummary.state_tax_rate_pct.toFixed(2)}%` : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {gstInvoiceSummary.state_tax_amount > 0 ? gstInvoiceSummary.state_tax_amount.toFixed(2) : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {gstInvoiceSummary.integrated_tax_rate_pct > 0
                          ? `${gstInvoiceSummary.integrated_tax_rate_pct.toFixed(2)}%`
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm">
                        {gstInvoiceSummary.integrated_tax_amount > 0
                          ? gstInvoiceSummary.integrated_tax_amount.toFixed(2)
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm font-medium">
                        {gstInvoiceSummary.total_tax_amount > 0 ? gstInvoiceSummary.total_tax_amount.toFixed(2) : "—"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{gstInvoiceSummary.total_quantity_display || "—"}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
              {gstInvoiceSummary.amount_chargeable_in_words.trim() || gstInvoiceSummary.tax_amount_in_words.trim() ? (
                <div className="text-xs text-muted-foreground space-y-1 max-w-3xl border-l-2 pl-3">
                  {gstInvoiceSummary.amount_chargeable_in_words.trim() ? (
                    <p>
                      <span className="font-medium text-foreground">Amount (in words): </span>
                      {gstInvoiceSummary.amount_chargeable_in_words}
                    </p>
                  ) : null}
                  {gstInvoiceSummary.tax_amount_in_words.trim() ? (
                    <p>
                      <span className="font-medium text-foreground">Tax (in words): </span>
                      {gstInvoiceSummary.tax_amount_in_words}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>

            <div className="space-y-2">
              <div className="font-medium">Items</div>
              <p className="text-xs text-muted-foreground">
                Per-line CGST/SGST/IGST columns are only when the invoice prints tax per row; otherwise they stay 0 and
                the footer summary above applies to the whole purchase.
              </p>
              <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product mapping</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Purchase</TableHead>
                    <TableHead className="text-right">Selling price</TableHead>
                    <TableHead className="text-right">MRP</TableHead>
                    <TableHead className="text-right text-xs whitespace-nowrap">CGST %</TableHead>
                    <TableHead className="text-right text-xs whitespace-nowrap">CGST ₹</TableHead>
                    <TableHead className="text-right text-xs whitespace-nowrap">SGST %</TableHead>
                    <TableHead className="text-right text-xs whitespace-nowrap">SGST ₹</TableHead>
                    <TableHead className="text-right text-xs whitespace-nowrap">IGST %</TableHead>
                    <TableHead className="text-right text-xs whitespace-nowrap">IGST ₹</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it, idx) => (
                    <TableRow
                      key={idx}
                      className={lowConfidenceItemRows.has(idx) || mappingRequiredItemRows.has(idx) ? "bg-destructive/5" : undefined}
                    >
                      <TableCell className="min-w-0 align-top md:min-w-[200px]">
                        <div className="space-y-2 w-full min-w-0">
                          <div className="flex items-center gap-2">
                            {it.product_id && (it.mapping_confidence ?? 0) > 0.9 ? (
                              <Badge>Auto-matched</Badge>
                            ) : it.product_id && (it.mapping_confidence ?? 0) >= 0.7 ? (
                              <Badge variant="secondary">Suggested — verify</Badge>
                            ) : (
                              <Badge variant="destructive">Needs mapping</Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {(Math.max(0, Math.min(1, it.mapping_confidence ?? 0)) * 100).toFixed(0)}%
                            </span>
                            {it.product_id ? (
                              <button
                                type="button"
                                className="ml-auto text-xs underline text-muted-foreground hover:text-foreground"
                                onClick={() => setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, product_id: null } : x)))}
                              >
                                Discard
                              </button>
                            ) : null}
                          </div>

                          <ProductSearchDropdown
                            value={
                              it.product_id
                                ? { id: it.product_id, name: it.product_name ?? `Product #${it.product_id}`, sku: null }
                                : null
                            }
                            onChange={(opt) => {
                              setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, product_id: opt ? opt.id : null } : x)));
                            }}
                            placeholder="Search product…"
                          />
                        </div>
                        {mappingRequiredItemRows.has(idx) ? (
                          <div className="text-xs text-destructive mt-1">
                            Product required (confidence {(Math.max(0, Math.min(1, it.mapping_confidence ?? 0)) * 100).toFixed(0)}%).
                          </div>
                        ) : null}
                      </TableCell>
                      <TableCell className="min-w-0 md:min-w-[180px]">
                        <Input
                          value={it.name}
                          onChange={(e) => setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))}
                        />
                      </TableCell>
                      <TableCell className="text-right min-w-0 md:min-w-[96px]">
                        <Input
                          value={String(it.qty)}
                          onChange={(e) =>
                            setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, qty: Number(e.target.value) } : x)))
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right min-w-0 md:min-w-[112px]">
                        <Input
                          value={String(it.purchase_price)}
                          onChange={(e) =>
                            setItems((prev) =>
                              prev.map((x, i) => (i === idx ? { ...x, purchase_price: Number(e.target.value) } : x)),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right min-w-0 md:min-w-[112px]">
                        <Input
                          value={String(it.selling_price)}
                          onChange={(e) =>
                            setItems((prev) =>
                              prev.map((x, i) => (i === idx ? { ...x, selling_price: Number(e.target.value) } : x)),
                            )
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right min-w-0 md:min-w-[112px]">
                        <Input
                          value={String(it.mrp)}
                          onChange={(e) =>
                            setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, mrp: Number(e.target.value) } : x)))
                          }
                        />
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums text-muted-foreground min-w-0 whitespace-nowrap md:min-w-[64px]">
                        {(it.cgst_rate_pct ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums text-muted-foreground min-w-0 whitespace-nowrap md:min-w-[64px]">
                        {(it.cgst_amount ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums text-muted-foreground min-w-0 whitespace-nowrap md:min-w-[64px]">
                        {(it.sgst_rate_pct ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums text-muted-foreground min-w-0 whitespace-nowrap md:min-w-[64px]">
                        {(it.sgst_amount ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums text-muted-foreground min-w-0 whitespace-nowrap md:min-w-[64px]">
                        {(it.igst_rate_pct ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right text-xs tabular-nums text-muted-foreground min-w-0 whitespace-nowrap md:min-w-[64px]">
                        {(it.igst_amount ?? 0).toFixed(2)}
                      </TableCell>
                      <TableCell className="text-right min-w-0 md:min-w-[72px]">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Remove row"
                          onClick={() => removeItemRow(idx)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-muted-foreground min-w-0">
                Status: <span className="font-mono">{status ?? "-"}</span>
                {status === "processing" ? (
                  <div className="mt-2 space-y-1 max-w-md">
                    <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div className="h-2 w-1/2 rounded-full bg-primary animate-pulse" />
                    </div>
                    <span className="text-xs">Processing with AI…</span>
                  </div>
                ) : null}
                {status === "processing" && autoRefreshStopped ? (
                  <span className="block text-xs mt-1">Auto-refresh stopped; click Refresh preview.</span>
                ) : null}
              </div>
              <Button type="button" className="min-h-11 shrink-0" onClick={() => void onCommit()} disabled={!canCommit}>
                {committing ? "Committing..." : "Commit to inventory"}
              </Button>
            </div>
              </div>
              {previewObjectUrl ? (
                <aside className="w-full shrink-0 space-y-2 lg:max-w-[min(100%,26rem)] lg:sticky lg:top-4">
                  <div className="text-sm font-medium text-foreground">Invoice image</div>
                  <div className="overflow-hidden rounded-lg border bg-muted">
                    <img src={previewObjectUrl} alt="" className="max-h-[70vh] w-full object-contain" />
                  </div>
                </aside>
              ) : null}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

